import axios from 'axios';
/* eslint-disable import/no-cycle */
import { adminApi } from '@/includes/contentManagementApi';

export default class NetworkAdmin {
  static getAdminAssessmentSchemas(config) {
    return axios.get('admin/assessmentSchemas', config);
  }

  static getAdminAssessment(config) {
    return axios.get('admin/assessments', config);
  }

  static getAdminContentElement(config) {
    return axios.get('admin/contentElement', config);
  }

  static getAdminConfigElement(config) {
    return axios.get('admin/configElement', config);
  }

  static getAdminUserElements(config) {
    return axios.get('admin/userElements', config);
  }

  static getAdminTaskElements(config) {
    return axios.get('admin/taskElements', config);
  }

  static async saveAdminAssessmentSchemas(data, config) {
    return axios.post('admin/assessmentSchemas', { data }, config);
  }

  static async saveAdminAssessment(data, config) {
    return axios.post('admin/assessments', { data }, config);
  }

  static async saveAdminConfigElement(data, config) {
    return axios.post('admin/configElement', { data }, config);
  }

  static async saveAdminContentElement(data, config) {
    return axios.post('admin/contentElement', { data }, config);
  }

  static async saveAdminUserElements(data, config) {
    return axios.post('admin/userElements', { data }, config);
  }

  static async saveAdminTaskElements(data, config) {
    return axios.post('admin/taskElements', { data }, config);
  }

  static async deleteUser(data, config) {
    // Migrated to Monorepo bulk delete endpoint
    // POST /content-management-api/user/bulk-delete
    return adminApi.post('user/bulk-delete', data, config);
  }

  static async postFilteredUsers(data, config) {
    // Migrated to Monorepo endpoint
    // POST /content-management-api/user/filtered
    return adminApi.post('user/filtered', data, config);
  }

  /**
   * Create assessments for users.
   * Routes to type-specific Monorepo endpoints for types with special logic,
   * uses generic Monorepo endpoint for all other types.
   * NO Task documents are created - only Assessments with seriesId.
   *
   * @param {Object} data - Assessment creation data
   * @param {string} data.schemaName - Assessment type (kss, mindset360, workshop, mindset, etc.)
   * @param {string[]} data.userIDs - Array of user IDs to create assessments for
   * @param {string} [data.startingTime] - Start time for the assessment
   * @param {string} [data.taskName] - Name of the assessment/meeting
   * @param {string} [data.taskDescription] - Description of the assessment
   * @param {boolean} [data.forceSimilarTask] - Force creation even if similar tasks exist
   * (KSS only)
   * @param {string} [data.schemaID] - Schema ID for specific schema selection
   * @param {Object} config - Axios config
   * @returns {Promise} API response
   */
  static async createTask(data, config) {
    const {
      schemaName,
      userIDs,
      startingTime,
      taskName,
      taskDescription,
      forceSimilarTask,
      schemaID,
    } = data;

    switch (schemaName?.toLowerCase()) {
      case 'kss':
        // Route to Monorepo KSS endpoint with transformed DTO
        // KSS has special logic for similar task detection, recurring meetings, etc.
        return adminApi.post('assessments/kss/create', {
          involvedUsers: userIDs,
          date_taskBegin: startingTime,
          name: taskName || '',
          description: taskDescription || '',
          forceSimilarTask: forceSimilarTask ?? true,
        }, config);

      case 'mindset360':
        // Route to generic Monorepo endpoint for admin-created external evaluations
        // The externalEvaluation/create endpoint has participant count and self-creation
        // validations that should NOT apply when admin is explicitly selecting users
        return adminApi.post('assessments/admin/create-from-schema', {
          schemaType: 'mindset360',
          schemaId: schemaID,
          userIDs,
          startingTime,
          name: taskName || 'External Evaluation',
          description: taskDescription || '',
        }, config);

      case 'workshop':
        // Route to generic Monorepo endpoint to create workshop reflexion assessments
        // NOT to /workshop endpoint which creates Workshop entities (different flow)
        return adminApi.post('assessments/admin/create-from-schema', {
          schemaType: 'workshop',
          schemaId: schemaID,
          userIDs,
          startingTime,
          name: taskName || 'Workshop Reflexion',
          description: taskDescription || '',
        }, config);

      default:
        // Use generic Monorepo endpoint for all other types
        // Creates Assessment documents directly with seriesId - NO Task documents
        return adminApi.post('assessments/admin/create-from-schema', {
          schemaType: schemaName,
          schemaId: schemaID,
          userIDs,
          startingTime,
          name: taskName || '',
          description: taskDescription || '',
        }, config);
    }
  }

  static getDetailedUserInfos(id, config) {
    // Migrated to Monorepo endpoint
    // GET /content-management-api/user/detailedUserInfo/:id
    return adminApi.get(`user/detailedUserInfo/${id}`, config);
  }

  static async postDownloadUserCSV(data, config) {
    // Migrated to Monorepo endpoint
    // POST /content-management-api/user/downloadUsersCSV
    return adminApi.post('user/downloadUsersCSV', data, {
      ...config,
      responseType: 'blob',
    });
  }

  static async getFilterComponentData(config) {
    return axios.get('admin/commands/filterComponentData', config);
  }

  static async uploadConfig(configData) {
    return axios.put('setup/initSetup', configData);
  }
}
